/**
 * PenYoloDetector — onnxruntime-web inference for the trained pen (+ exam
 * object) YOLO detector. INTEGRATION SCAFFOLD (2026-06-09): the model file
 * `pen_detect.onnx` does not exist yet — train it via ml_pen_detect/ and drop
 * the export into public/. Until then this loads lazily and fails gracefully
 * (detect → []), and it is OFF by default in the app (`?penOd=on`).
 *
 * NOTE: distinct from the legacy image-processing `PenObjectDetector.ts`
 * (dark-mass heuristic). This is the ML object-detector path Kay requested.
 *
 * Output of `ml_pen_detect/export_onnx.py` uses ultralytics `nms=True`, so the
 * ONNX graph emits FINAL boxes — expected output tensor [1, N, 6], each row =
 * [x1, y1, x2, y2, confidence, class_id] in letterboxed input pixels. (Run
 * export_onnx.py's self-test, which prints the real output shape; adjust
 * `decode()` if it differs.)
 *
 * Returns RawDetection[] (ObjectTracker input shape) so a detected pen flows
 * into the same tracking / identity / occlusion memory layer as phone/book.
 */
import * as ort from 'onnxruntime-web';
import type { RawDetection } from './ObjectTracker';
// eslint-disable-next-line @typescript-eslint/no-require-imports
const ortRt = require('onnxruntime-web/wasm') as typeof ort;

const ASSET_BASE = '/static';
ortRt.env.wasm.wasmPaths = `${ASSET_BASE}/`;
ortRt.env.wasm.numThreads = 1;
ortRt.env.wasm.simd = true;
ortRt.env.wasm.proxy = false;
ortRt.env.logLevel = 'warning';

// Default name reserved for the commercial-clean pen model (torchvision BSD-3
// fine-tune; weights VALIDATED 2026-07-13: gen recall 82.5%, small-pen 74.1%).
// The file is NOT in public/ yet — FasterRCNN ONNX export fails on both torch
// exporters (data-dependent detection postprocessing), so the browser vehicle
// is pending (options in docs/pen_failure_diagnosis_2026_0712.md §5b; the
// heavy detector may live server-side in the engine cloud instead). Until
// then this detector fail-softs (off-default + missing model = inactive).
// `?penModel=<name>` = local A/B escape hatch.
const MODEL_NAME =
  new URLSearchParams(window.location.search).get('penModel') || 'pen_detect_commercial';
const MODEL_URL = `${ASSET_BASE}/${MODEL_NAME}.onnx`;
const IMGSZ = 640;

// ── FP-reduction gates (tunable via URL for on-device A/B) ──
// Offline eval on the exam recordings (laeo_eval/pen_video_eval.py) showed the
// v1 model fires mostly on torso / screen-UI with near-square boxes; real pens
// are thin (elongated) and small. An elongation (aspect) gate alone removed
// ~94-95% of those false positives; conf-only did not (UI panels score high).
//   ?penConf=0.25     min confidence (default 0.25; retrained domain model emits
//                     clean pen-shaped boxes at 0.15–0.76, so the shape/area gates
//                     — not a high conf floor — are the FP defense. 2026-06-22:
//                     old 0.50 floor dropped ~half of real pens to "miss".)
//   ?penAspect=2.5    min bbox elongation max(w,h)/min(w,h) (pens are thin)
//   ?penMaxArea=0.03  max bbox area fraction (pens are small in frame)
//   ?penShapeOff      disable shape/area gates (conf-only baseline for A/B)
const _penSearch = typeof window !== 'undefined' ? window.location.search : '';
function _penNum(key: string, def: number): number {
  const m = _penSearch.match(new RegExp(`[?&]${key}=([0-9.]+)`));
  return m ? parseFloat(m[1]) : def;
}
const CONF_THRESHOLD = _penNum('penConf', 0.25);
const SHAPE_GATE = !_penSearch.includes('penShapeOff');
const MIN_ASPECT = _penNum('penAspect', 2.5);
const MAX_AREA = _penNum('penMaxArea', 0.03);

// ── temporal smoothing (flicker suppression, no retraining) ──
//   ?penSmoothOff   disable temporal confirmation (raw per-frame boxes)
//   ?penMinHits=3   consecutive detections required before a pen is emitted
//   ?penMaxMiss=2   missed detections tolerated before a track is dropped
const SMOOTH = !_penSearch.includes('penSmoothOff');
const MIN_HITS = _penNum('penMinHits', 3);
const MAX_MISS = _penNum('penMaxMiss', 2);

// ── hand-ROI cropping — run inference on the union+margin crop around the
//    hand(s) instead of the full frame. Focuses on where a held pen is (kills
//    torso/UI false positives) and upsamples the small pen region (recall↑).
//    Falls back to full frame when no hand is provided. ──
//   ?penRoiOff       disable (full-frame inference)
//   ?penRoiMargin=0.4 expand union hand box by this fraction each side
//   ?penRoiMin=0.25   minimum crop size (frame fraction) so tiny hands aren't over-zoomed
const ROI_ON = !_penSearch.includes('penRoiOff');
const ROI_MARGIN = _penNum('penRoiMargin', 0.4);
const ROI_MIN = _penNum('penRoiMin', 0.25);

// class index → labels. Keep in sync with ml_pen_detect/data.yaml `names`.
const CLASS_MAP: Record<number, { label: string; labelKo: string }> = {
  0: { label: 'pen', labelKo: '펜' },
  // 1: { label: 'pencil', labelKo: '연필' },
  // 2: { label: 'cell phone', labelKo: '핸드폰' },
  // 3: { label: 'book', labelKo: '교재/책' },
  // 4: { label: 'eraser', labelKo: '지우개' },
  // 5: { label: 'calculator', labelKo: '계산기' },
};

export type PenYoloStatus = 'idle' | 'loading' | 'ready' | 'failed';
let modelStatus: PenYoloStatus = 'idle';
let modelError: string | null = null;
export function getPenYoloStatus(): { status: PenYoloStatus; error: string | null } {
  return { status: modelStatus, error: modelError };
}

let sessionPromise: Promise<ort.InferenceSession> | null = null;
function loadSession(): Promise<ort.InferenceSession> {
  if (!sessionPromise) {
    modelStatus = 'loading';
    sessionPromise = ortRt.InferenceSession.create(MODEL_URL, {
      executionProviders: ['wasm'],
      graphOptimizationLevel: 'all',
    }).then(s => {
      modelStatus = 'ready';
      // eslint-disable-next-line no-console
      console.log('[PenYoloDetector] ONNX loaded', s.inputNames, '→', s.outputNames);
      return s;
    }).catch(err => {
      modelStatus = 'failed';
      modelError = err?.message ?? String(err);
      // eslint-disable-next-line no-console
      console.warn('[PenYoloDetector] model not loaded (train + place pen_detect.onnx):', modelError);
      throw err;
    });
  }
  return sessionPromise;
}

let inflight = false;
let lastResult: RawDetection[] = [];
export function detectPenYoloSync(): RawDetection[] { return lastResult; }

// ── temporal tracker: confirm a pen only after it persists across frames ──
interface PenTrack { x: number; y: number; w: number; h: number; score: number; hits: number; miss: number; label: string; labelKo: string; }
let tracks: PenTrack[] = [];
function iou(a: { x: number; y: number; w: number; h: number }, b: PenTrack): number {
  const ix = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
  const iy = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
  const inter = ix * iy;
  const uni = a.w * a.h + b.w * b.h - inter;
  return uni > 0 ? inter / uni : 0;
}
/** Match raw dets to tracks (IoU), age unmatched, emit tracks seen >=MIN_HITS
 *  (coasts 1 missed frame). Kills single-frame flicker FPs; EMA-stabilizes boxes. */
function smooth(dets: RawDetection[]): RawDetection[] {
  const prevLen = tracks.length;
  const matched = new Array<boolean>(prevLen).fill(false);
  for (const d of dets) {
    let best = -1, bestIoU = 0.3;
    for (let i = 0; i < prevLen; i++) {
      if (matched[i]) continue;
      const v = iou(d.bboxNorm, tracks[i]);
      if (v > bestIoU) { bestIoU = v; best = i; }
    }
    if (best >= 0) {
      const t = tracks[best]; matched[best] = true;
      const a = 0.5;  // EMA box smoothing
      t.x = a * d.bboxNorm.x + (1 - a) * t.x;
      t.y = a * d.bboxNorm.y + (1 - a) * t.y;
      t.w = a * d.bboxNorm.w + (1 - a) * t.w;
      t.h = a * d.bboxNorm.h + (1 - a) * t.h;
      t.score = Math.max(t.score * 0.7, d.score);
      t.hits++; t.miss = 0; t.label = d.label; t.labelKo = d.labelKo;
    } else {
      const b = d.bboxNorm;
      tracks.push({ x: b.x, y: b.y, w: b.w, h: b.h, score: d.score, hits: 1, miss: 0, label: d.label, labelKo: d.labelKo });
    }
  }
  for (let i = 0; i < prevLen; i++) if (!matched[i]) tracks[i].miss++;
  tracks = tracks.filter(t => t.miss <= MAX_MISS);
  return tracks
    .filter(t => t.hits >= MIN_HITS && t.miss <= 1)
    .map(t => ({ label: t.label, labelKo: t.labelKo, score: t.score, bboxNorm: { x: t.x, y: t.y, w: t.w, h: t.h } }));
}

export interface PenRoi { x: number; y: number; w: number; h: number; }

/**
 * Single-flight async detection on the current video frame. Returns the most
 * recent result immediately and kicks off a new inference if idle (so it
 * never blocks the render/tick loop). Empty array until the model is ready.
 */
export async function detectPenYolo(
  video: HTMLVideoElement,
  offCanvas: HTMLCanvasElement,
  handRois?: PenRoi[],
): Promise<RawDetection[]> {
  const vw = video.videoWidth, vh = video.videoHeight;
  if (!vw || !vh) return lastResult;
  if (inflight) return lastResult;
  inflight = true;
  try {
    const session = await loadSession();

    // ── choose source region: hand-ROI crop (union+margin) or full frame ──
    let sx = 0, sy = 0, sw = vw, sh = vh;
    if (ROI_ON && handRois && handRois.length) {
      let x0 = 1, y0 = 1, x1 = 0, y1 = 0;
      for (const r of handRois) {
        x0 = Math.min(x0, r.x); y0 = Math.min(y0, r.y);
        x1 = Math.max(x1, r.x + r.w); y1 = Math.max(y1, r.y + r.h);
      }
      const mx = (x1 - x0) * ROI_MARGIN, my = (y1 - y0) * ROI_MARGIN;
      x0 -= mx; x1 += mx; y0 -= my; y1 += my;
      if (x1 - x0 < ROI_MIN) { const c = (x0 + x1) / 2; x0 = c - ROI_MIN / 2; x1 = c + ROI_MIN / 2; }
      if (y1 - y0 < ROI_MIN) { const c = (y0 + y1) / 2; y0 = c - ROI_MIN / 2; y1 = c + ROI_MIN / 2; }
      x0 = Math.max(0, x0); y0 = Math.max(0, y0); x1 = Math.min(1, x1); y1 = Math.min(1, y1);
      sx = x0 * vw; sy = y0 * vh; sw = (x1 - x0) * vw; sh = (y1 - y0) * vh;
    }

    // ── Letterbox the source region into IMGSZ×IMGSZ (preserve aspect) ──
    const scale = Math.min(IMGSZ / sw, IMGSZ / sh);
    const newW = Math.round(sw * scale), newH = Math.round(sh * scale);
    const padX = Math.floor((IMGSZ - newW) / 2), padY = Math.floor((IMGSZ - newH) / 2);
    if (offCanvas.width !== IMGSZ || offCanvas.height !== IMGSZ) {
      offCanvas.width = IMGSZ; offCanvas.height = IMGSZ;
    }
    const ctx = offCanvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return lastResult;
    ctx.fillStyle = 'rgb(114,114,114)';
    ctx.fillRect(0, 0, IMGSZ, IMGSZ);
    ctx.drawImage(video, sx, sy, sw, sh, padX, padY, newW, newH);
    const img = ctx.getImageData(0, 0, IMGSZ, IMGSZ).data;

    // ── HWC RGBA uint8 → NCHW RGB float32 /255 (ultralytics default) ──
    const n = IMGSZ * IMGSZ;
    const chw = new Float32Array(3 * n);
    for (let i = 0; i < n; i++) {
      chw[i] = img[i * 4] / 255;             // R
      chw[n + i] = img[i * 4 + 1] / 255;     // G
      chw[2 * n + i] = img[i * 4 + 2] / 255; // B
    }
    const input = new ortRt.Tensor('float32', chw, [1, 3, IMGSZ, IMGSZ]);
    const feeds: Record<string, ort.Tensor> = { [session.inputNames[0]]: input };
    const outMap = await session.run(feeds);
    const t = outMap[session.outputNames[0]];

    const raw = decode(t, vw, vh, scale, padX, padY, sx, sy);
    lastResult = SMOOTH ? smooth(raw) : raw;
    return lastResult;
  } catch {
    // model missing/failed → graceful empty (still age tracks so stale boxes clear)
    lastResult = SMOOTH ? smooth([]) : [];
    return lastResult;
  } finally {
    inflight = false;
  }
}

/** Decode ultralytics nms=True output [1, N, 6] → normalized RawDetection[].
 *  (sx,sy) = source-region origin in full-frame pixels (0,0 for full frame). */
function decode(
  t: ort.Tensor, vw: number, vh: number,
  scale: number, padX: number, padY: number, sx: number, sy: number,
): RawDetection[] {
  const dims = t.dims as readonly number[];
  const data = t.data as Float32Array;
  // Expect [1, N, 6]; bail clearly otherwise (see export self-test shape).
  if (dims.length !== 3 || dims[2] !== 6) {
    // eslint-disable-next-line no-console
    console.warn('[PenYoloDetector] unexpected output shape', dims,
      '— expected [1,N,6] (ultralytics nms=True). Adjust decode().');
    return [];
  }
  const N = dims[1];
  const dets: RawDetection[] = [];
  for (let i = 0; i < N; i++) {
    const o = i * 6;
    const conf = data[o + 4];
    if (conf < CONF_THRESHOLD) continue;
    const cls = Math.round(data[o + 5]);
    const meta = CLASS_MAP[cls];
    if (!meta) continue;
    // letterboxed-pixel xyxy → source-region pixel → full-frame normalized [0,1]
    const x1 = (sx + (data[o] - padX) / scale) / vw;
    const y1 = (sy + (data[o + 1] - padY) / scale) / vh;
    const x2 = (sx + (data[o + 2] - padX) / scale) / vw;
    const y2 = (sy + (data[o + 3] - padY) / scale) / vh;
    const x = Math.max(0, Math.min(1, x1));
    const y = Math.max(0, Math.min(1, y1));
    const w = Math.max(0, Math.min(1, x2)) - x;
    const h = Math.max(0, Math.min(1, y2)) - y;
    if (w <= 0 || h <= 0) continue;
    // FP gate: real pens are thin (elongated) and small in frame; the v1
    // model's FPs are blobby torso/UI boxes. Reject non-pen-shaped boxes.
    if (SHAPE_GATE) {
      const aspect = Math.max(w, h) / Math.max(1e-6, Math.min(w, h));
      if (aspect < MIN_ASPECT || w * h > MAX_AREA) continue;
    }
    dets.push({ label: meta.label, labelKo: meta.labelKo, score: conf, bboxNorm: { x, y, w, h } });
  }
  return dets;
}
